import { useCallback, useState } from 'react'
import {
  ICarrierApplication,
  ISubmitBrokerCarrierAgreementPayload,
  ISubmitCarrierPackageFiles,
  IUpdateCarrierReferences,
} from 'types'
import { BooleanParam } from 'use-query-params'
import { useFeatureFlag } from 'utils/context/FeatureFlagsContext'
import { IMobileWebViewParams } from 'apps/fleet-planner/types/mobileApp'
import { useFetchData, useQueryParams } from 'utils/hooks'
import { STEPS } from '../components/activation/Steps'
import { EActivationStep } from '../components/activation/constants'
import { useCarrierApplicationQuery } from '../components/activation/graphql/CarrierApplication'
import {
  IMotorCarrierInsuranceContactsQuery,
  useMotorCarrierInsuranceContactsQuery,
} from '../pages/carrier-profile/components/InsuranceContactInformationCard/graphql/InsuranceContactsQuery'
import {
  fetchMarketplaceCarrierApplication,
  submitBrokerCarrierAgreement,
  submitCarrierPackageFiles,
  updateCarrierReferences,
  sendCarrierPacketEmail,
  sendDocumentReviewEmail,
} from '../utils/api/activate'
import {
  getHasCompletedActivationSteps,
  getCurrentActivationStep,
  getNewCurrentActivationStep,
  getNewHasCompletedActivationSteps,
} from '../utils/getActivationStep'

export const useActivation = () => {
  const { errors, fetchData, isLoading } = useFetchData()
  const [carrierDetails, setCarrierDetails] = useState<ICarrierApplication>(
    {} as ICarrierApplication
  )
  const isFmsHighwayCarrierOnboardingEnabled = useFeatureFlag('fmsHighwayCarrierOnboarding')
  const isCarrierAppMilestoneFiveCarrierUploadEnabled = useFeatureFlag(
    'carrierAppMilestoneFiveCarrierUpload'
  )
  const [currentStep, setCurrentStep] = useState<keyof typeof STEPS>(EActivationStep.bca)
  const [showActivationToast, setShowActivationToast] = useState<boolean>(false)
  const { fleetPlannerMotorCarrierId, id: carrierApplicationId } = carrierDetails || {}

  const { data: carrierApplicationData } = useCarrierApplicationQuery({
    variables: {
      id: carrierApplicationId,
    },
    skip: !carrierApplicationId,
  })
  const { carrierApplication } = carrierApplicationData || {}
  const { hasW9, safetyLetterNeeded } = carrierApplication || {}

  const { data: insuranceContactData } = useMotorCarrierInsuranceContactsQuery({
    variables: {
      motorCarrierId: fleetPlannerMotorCarrierId,
    },
    skip: !fleetPlannerMotorCarrierId,
  })

  const getMarketplaceCarrierApplication = useCallback(
    async (id: string) => {
      const { carrierApplication } = (await fetchData(fetchMarketplaceCarrierApplication(id))) || {}
      setCarrierDetails(carrierApplication || {})
      const activationSteps = getHasCompletedActivationSteps(carrierApplication || {})
      const newActivationSteps = getNewHasCompletedActivationSteps(
        carrierApplication || {},
        carrierApplicationData || {},
        insuranceContactData as IMotorCarrierInsuranceContactsQuery,
        isCarrierAppMilestoneFiveCarrierUploadEnabled
      )
      const oldInitialStep = getCurrentActivationStep(activationSteps)
      const newInitialStep = getNewCurrentActivationStep(newActivationSteps)
      const initialStep = isFmsHighwayCarrierOnboardingEnabled ? newInitialStep : oldInitialStep
      setCurrentStep(initialStep)
    },
    [fetchData, carrierApplicationData, insuranceContactData]
  )
  const [{ mobileApp }] = useQueryParams<IMobileWebViewParams>({ mobileApp: BooleanParam })

  const submitBca = useCallback(
    async (id: string, payload: ISubmitBrokerCarrierAgreementPayload) =>
      fetchData(submitBrokerCarrierAgreement(id, payload)),
    [fetchData]
  )

  const submitPackageFiles = useCallback(
    async (id: string, packageFiles: ISubmitCarrierPackageFiles) =>
      fetchData(submitCarrierPackageFiles(id, packageFiles)),
    [fetchData]
  )

  const updateReferences = useCallback(
    async (id: string, references: IUpdateCarrierReferences) =>
      fetchData(updateCarrierReferences(id, references)),
    [fetchData]
  )

  const sendPacketEmail = useCallback(async (id: string) => fetchData(sendCarrierPacketEmail(id)), [
    fetchData,
  ])

  const sendDocumentEmail = useCallback(
    async (id: string) => fetchData(sendDocumentReviewEmail(id)),
    [fetchData]
  )

  const activationSteps = isCarrierAppMilestoneFiveCarrierUploadEnabled
    ? getNewHasCompletedActivationSteps(
        carrierDetails || {},
        carrierApplicationData || {},
        insuranceContactData as IMotorCarrierInsuranceContactsQuery,
        isCarrierAppMilestoneFiveCarrierUploadEnabled
      )
    : getHasCompletedActivationSteps(carrierDetails)

  return {
    activationSteps,
    hasW9,
    insuranceContacts: insuranceContactData,
    isLoading,
    errors,
    getMarketplaceCarrierApplication,
    carrierDetails,
    submitBca,
    submitPackageFiles,
    updateReferences,
    currentStep,
    isSafetyLetterNeeded: safetyLetterNeeded,
    setCurrentStep,
    sendPacketEmail,
    sendDocumentEmail,
    showActivationToast,
    setShowActivationToast,
    mobileApp,
  }
}
